// Group reconciliation uses held history and the same renderer as live delivery.
import type { WSMessage } from './types';
import type { Roll } from '../log/types';
import { heldRollRows } from './roll-update';
import { _ftFindLogRowByRollId, FT_LOG_VISIBLE_ROW_CAP, prependLogRow, trimLogRows } from './log-row-helpers';

type WSRollGroupCompleteMessage = Extract<WSMessage, { type: 'roll_group_complete' }>;

/** Replace only this group's nodes at their existing position. History rendering emits no cues. */
export function replaceRollGroup(rid: string, rolls: Roll[], old: HTMLElement[]): boolean {
    const render = window.renderLogWindowFromJson;
    if (typeof render !== 'function') return false;
    const built = render({ rolls, campaign_permission: window.FateTide?.user?.isGM ? 1 : 0 });
    if (!built) return false;
    const open = new Set(old.flatMap(node => [...node.querySelectorAll('.collapse.show')].map(el => el.id)).filter(Boolean));
    const template = document.createElement('template');
    template.innerHTML = built.html;
    const fresh = [...template.content.querySelector('ul')?.children ?? []] as HTMLElement[];
    if (fresh.length && !document.querySelector('#div_sidelog_results > ul.list-group')) prependLogRow('');
    const ul = document.querySelector('#div_sidelog_results > ul.list-group');
    if (!ul) return fresh.length === 0;
    for (const node of fresh) {
        node.dataset.ftLiveGroup = rid;
        node.dataset.ftRollGroupState = 'settled';
    }
    for (const id of open) {
        const collapse = template.content.getElementById(id);
        collapse?.classList.add('show');
        template.content.querySelector(`[data-bs-target="#${CSS.escape(id)}"]`)?.setAttribute('aria-expanded', 'true');
    }
    // Keep the state of controls whose identity survives this group's reconciliation.
    for (const node of old) for (const control of node.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input[id], select[id], textarea[id]')) {
        const next = template.content.getElementById(control.id) as typeof control | null;
        if (!next || next.tagName !== control.tagName) continue;
        next.value = control.value;
        next.disabled = control.disabled;
        if (control instanceof HTMLInputElement && next instanceof HTMLInputElement) next.checked = control.checked;
    }
    for (const node of old) for (const button of node.querySelectorAll<HTMLButtonElement>('button[data-ft-log-action][disabled]')) {
        for (const next of template.content.querySelectorAll<HTMLButtonElement>('button[data-ft-log-action]')) {
            if ([...button.attributes].filter(attr => attr.name.startsWith('data-ft-')).every(attr => next.getAttribute(attr.name) === attr.value)) next.disabled = true;
        }
    }
    if (old.length) {
        old[0]!.before(...fresh);
        for (const node of old) {
            if (typeof bootstrap !== 'undefined' && bootstrap.Tooltip) node.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
                try { bootstrap.Tooltip.getInstance(el)?.dispose(); } catch { /* already disposed */ }
            });
            node.remove();
        }
    } else ul.prepend(...fresh);
    for (const node of fresh) {
        if (typeof bootstrap !== 'undefined' && bootstrap.Tooltip) node.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
            try { new bootstrap.Tooltip(el); } catch { /* already mounted */ }
        });
        try { window.ftMountRuneIcons?.(node); } catch { /* rune bundle absent */ }
    }
    window.activateLogCollapseTasks?.(built.collapseLabelTasks ?? []);
    for (const id of open) {
        const collapse = fresh.flatMap(node => [...node.querySelectorAll('.collapse')]).find(node => node.id === id);
        collapse?.dispatchEvent(new Event('show.bs.collapse', { bubbles: true }));
    }
    trimLogRows(FT_LOG_VISIBLE_ROW_CAP);
    return true;
}

export function reconcileHeldRollGroup(rid: string, incoming?: Roll): boolean {
    const row = _ftFindLogRowByRollId(rid);
    if (!row) return false;
    const rolls: Roll[] = [];
    for (const row of heldRollRows()) {
        if (row.roll_id === rid && (!incoming || String(row.roll_i) !== String(incoming.roll_i))) rolls.push(row as unknown as Roll);
    }
    if (incoming) rolls.push(incoming);
    if (!rolls.length) return false;
    rolls.sort((a, b) => Number(b.roll_i) - Number(a.roll_i));
    const old: HTMLElement[] = [];
    for (const node of row.parentElement!.children) {
        if (node instanceof HTMLElement && node.dataset.ftRollId === rid) old.push(node);
    }
    return replaceRollGroup(rid, rolls, old);
}

// Modern groups already settled on arrival. A legacy compiling placeholder can still
// receive a completion signal; settle that exact group from held rows without a log read.
async function handleRollGroupComplete(msg: WSRollGroupCompleteMessage): Promise<void> {
    const rid = msg?.roll_id;
    if (rid && _ftFindLogRowByRollId(rid)?.dataset.ftRollGroupState === 'compiling') reconcileHeldRollGroup(rid);
}
export { handleRollGroupComplete };
declare global { interface Window { ftCompleteTruthRollGroup?: (operationId: string) => Promise<void> } }
window.ftCompleteTruthRollGroup = operationId => handleRollGroupComplete({ type: 'roll_group_complete', roll_id: operationId });
